import { ContentType } from 'domains/content/types';
import { useHistory, useParams } from 'react-router-dom';
import useQueryParams from './useQueryParams';
import { URLPaths } from 'models/enums';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

/**
 * A hook to get the type, folderId, status and the function to update them for the content page.
 * */
const useContentQueryManager = () => {
    const history = useHistory();
    const query = useQueryParams();
    const type = query.get('type') as ContentType;
    const statusParam = query.get('status');
    const statuses: ContentStatus[] = statusParam ? (statusParam.split(',') as ContentStatus[]) : [];
    let { folderId } = useParams<{ folderId: string | undefined }>();

    const numericFolderId = folderId === undefined ? -1 : Number(folderId);

    const basePath = `${URLPaths.CONTENT}`;

    const getFolderPath = (newFolderId: number) => {
        if (newFolderId) {
            return newFolderId === -1 ? '' : `${URLPaths.CONTENT_FOLDER}/${newFolderId}`;
        }
        return folderId ? `${URLPaths.CONTENT_FOLDER}/${folderId}` : '';
    };

    const buildQueryParams = (newType?: string, newStatuses?: ContentStatus[]) => {
        const params: string[] = [];
        const effectiveType = newType !== undefined ? newType : type;
        const effectiveStatuses = newStatuses !== undefined ? newStatuses : statuses;

        if (effectiveType && effectiveType !== ContentType.ALL) {
            params.push(`type=${effectiveType}`);
        }

        if (effectiveStatuses.length > 0) {
            params.push(`status=${effectiveStatuses.join(',')}`);
        }

        return params.length > 0 ? `?${params.join('&')}` : '';
    };

    const changeViewParams = ({
        newFolderId,
        newType,
        newStatuses,
    }: {
        newFolderId?: number;
        newType?: string;
        newStatuses?: ContentStatus[];
    } = {}) => {
        const folderPath = getFolderPath(newFolderId || numericFolderId);
        const queryParams = buildQueryParams(newType, newStatuses);

        // If we have a folder path, use it directly (it already includes the full path)
        // If no folder path, use the base content path
        const path = folderPath ? `${folderPath}${queryParams}` : `${basePath}${queryParams}`;

        history.push(path);
    };

    return { type, folderId: numericFolderId, statuses, changeViewParams };
};

export default useContentQueryManager;
